import { z } from 'zod';

export const PriceRangeSchema=z.object({
  min:z.number().nullable().optional(),
  max:z.number().nullable().optional(),
  currency:z.string().optional()
}).passthrough();

export const ProductSummarySchema=z.object({
  count:z.number().int().nonnegative(),
  sample:z.array(z.unknown()).default([]),
  priceRange:PriceRangeSchema.nullable().optional(),
  typicalPriceBand:z.object({low:z.number(),high:z.number()}).nullable().optional(),
  medianPrice:z.number().nullable().optional(),
  currency:z.string().optional(),
  catalogComplete:z.boolean().optional(),
  productsJsonCount:z.number().optional(),
  collectionObservedCount:z.number().nullable().optional()
}).passthrough();

export const WebsiteSchema=z.object({
  domain:z.string().optional(),
  finalUrl:z.string().optional(),
  title:z.string().optional(),
  description:z.string().optional(),
  currency:z.string().optional()
}).passthrough();

export const AnalyzeSiteSchema=z.object({
  ok:z.literal(true),
  observedAt:z.string().optional(),
  website:WebsiteSchema,
  technology:z.object({platform:z.string().nullable().optional()}).passthrough().optional(),
  products:ProductSummarySchema.optional(),
  social:z.unknown().optional()
}).passthrough();

export const CatalogSchema=z.object({
  ok:z.literal(true),
  observedAt:z.string().optional(),
  products:ProductSummarySchema
}).passthrough();

export const SerpSampleSchema=z.object({
  query:z.string(),
  rank:z.number().nullable().optional(),
  totalResults:z.number().nullable().optional()
}).passthrough();

export const IndependentSearchSchema=z.object({
  ok:z.literal(true),
  domain:z.string(),
  observedAt:z.string().optional(),
  status:z.string(),
  seo:z.object({
    classification:z.string().optional(),
    queries:z.array(z.string()).default([]),
    searchResults:z.object({
      pages:z.array(z.object({url:z.string(),title:z.string(),snippet:z.string(),position:z.number().nullable()})).default([]),
      resultsInSample:z.number().nonnegative(),
      topResultsChecked:z.number().nonnegative()
    }),
    serp:z.object({
      providerConnected:z.boolean(),
      samples:z.array(z.object({query:z.string(),rank:z.number().nullable(),totalResults:z.number().nullable()}))
    })
  }).passthrough().nullable(),
  limitations:z.array(z.string()).optional()
}).passthrough();

export type IndependentSearch=z.infer<typeof IndependentSearchSchema>;

export const SeoEstimateSchema=z.object({
  classification:z.string().optional(),
  sitemapUrls:z.number().nullable().optional(),
  indexedUrls:z.number().nullable().optional(),
  structure:z.record(z.string(),z.number()).nullable().optional(),
  pagesSampled:z.number().default(0),
  pagesWithTitles:z.number().default(0),
  pagesWithDescriptions:z.number().default(0),
  pagesWithH1:z.number().default(0),
  queries:z.array(z.string()).default([]),
  sitemapFreshness:z.object({
    urlsWithLastmod:z.number().nonnegative().optional(),
    updated30d:z.number().nonnegative().optional(),
    updated90d:z.number().nonnegative().optional(),
    updated365d:z.number().nonnegative().optional(),
    latestLastmod:z.string().nullable().optional(),
    coverageRatio:z.number().min(0).max(1).optional()
  }).passthrough().nullable().optional(),
  serp:z.object({
    providerConnected:z.boolean().optional(),
    samples:z.array(SerpSampleSchema).default([]),
    visibilityScore:z.number().default(0),
    appearances:z.number().default(0),
    bestRank:z.number().nullable().optional(),
    avgRank:z.number().nullable().optional()
  }).passthrough().optional(),
  confidence:z.number().min(0).max(100).optional()
}).passthrough();

export const TrafficEstimateSchema=z.object({
  low:z.number().nonnegative(),
  base:z.number().nonnegative(),
  high:z.number().nonnegative(),
  confidence:z.number().min(0).max(100),
  modelVersion:z.string(),
  inputs:z.object({
    productCount:z.number().nonnegative().optional(),
    sitemapUrls:z.number().nonnegative().optional(),
    indexedUrls:z.number().nonnegative().optional(),
    blogCount:z.number().nonnegative().optional(),
    collectionCount:z.number().nonnegative().optional(),
    seoCompleteness:z.number().optional(),
    serpVisibility:z.number().optional(),
    serpQueries:z.number().nonnegative().optional(),
    sitemapFreshness:z.object({
      updated90d:z.number().nonnegative().optional(),
      coverageRatio:z.number().min(0).max(1).optional(),
      latestLastmod:z.string().nullable().optional()
    }).passthrough().optional(),
    signalContributions:z.object({
      siteScale:z.number().optional(),
      commerceDepth:z.number().optional(),
      contentDepth:z.number().optional(),
      collectionDepth:z.number().optional(),
      freshness:z.number().optional(),
      serpLift:z.number().optional()
    }).passthrough().optional(),
    note:z.string().optional()
  }).passthrough()
}).passthrough();

export const RevenueEstimateSchema=z.object({
  period:z.string(),
  aov:z.number().nonnegative(),
  aovRange:z.object({low:z.number(),base:z.number(),high:z.number()}).optional(),
  cvr:z.object({low:z.number(),base:z.number(),high:z.number()}),
  orders:z.object({low:z.number().nonnegative(),base:z.number().nonnegative(),high:z.number().nonnegative()}),
  revenue:z.object({low:z.number().nonnegative(),base:z.number().nonnegative(),high:z.number().nonnegative()}),
  confidence:z.number().min(0).max(100),
  modelVersion:z.string(),
  inputs:z.object({
    medianCatalogPrice:z.number().nullable().optional(),
    typicalPriceBand:z.object({low:z.number(),high:z.number()}).nullable().optional(),
    freeShippingThreshold:z.unknown().optional(),
    traffic:z.unknown().optional(),
    note:z.string().optional()
  }).passthrough().optional()
}).passthrough();

export const EconomicsEstimateSchema=z.object({
  grossMargin:z.object({low:z.number(),base:z.number(),high:z.number()}),
  grossProfit:z.object({low:z.number(),base:z.number(),high:z.number()}),
  mer:z.number().nullable().optional(),
  cac:z.number().nullable().optional(),
  paidRoas:z.number().nullable().optional(),
  confidence:z.number().min(0).max(100),
  modelVersion:z.string(),
  note:z.string().optional()
}).passthrough();

export const MetricsSchema=z.object({
  ok:z.literal(true),
  observedAt:z.string().optional(),
  domain:z.string().optional(),
  catalog:ProductSummarySchema.optional(),
  seo:SeoEstimateSchema.optional(),
  traffic:TrafficEstimateSchema.optional(),
  revenue:RevenueEstimateSchema.optional(),
  economics:EconomicsEstimateSchema.optional(),
  persisted:z.boolean().optional(),
  provenance:z.object({
    classification:z.string().optional(),
    providers:z.array(z.string()).optional(),
    modelVersions:z.array(z.string()).optional()
  }).passthrough().optional()
}).passthrough();

export const BilingualTextSchema=z.object({en:z.string(),zh:z.string()}).passthrough();

export const PositioningPayloadSchema=z.object({
  category:BilingualTextSchema.optional(),
  subcategories:z.array(BilingualTextSchema).default([]),
  audience:BilingualTextSchema.optional(),
  painPoints:z.array(BilingualTextSchema).default([]),
  motivations:z.array(BilingualTextSchema).default([]),
  valueProp:BilingualTextSchema.optional(),
  confidence:z.number().optional(),
  evidence:z.array(z.object({signal:z.string().optional(),detail:BilingualTextSchema.optional()}).passthrough()).default([])
}).passthrough();

export const PositioningSchema=z.object({ok:z.literal(true),positioning:PositioningPayloadSchema.optional()}).passthrough();

export const SocialPostSchema=z.object({
  platform:z.string().optional(),
  url:z.string().optional(),
  id:z.string().optional(),
  caption:z.string().nullable().optional(),
  likes:z.number().nullable().optional(),
  comments:z.number().nullable().optional(),
  views:z.number().nullable().optional(),
  shares:z.number().nullable().optional(),
  timestamp:z.number().nullable().optional(),
  mediaType:z.string().nullable().optional()
}).passthrough();

export const SocialProfileSchema=z.object({
  platform:z.string(),
  handle:z.string().nullable().optional(),
  profileUrl:z.string().optional(),
  title:z.string().nullable().optional(),
  description:z.string().nullable().optional(),
  followers:z.number().nullable().optional(),
  following:z.number().nullable().optional(),
  posts:z.number().nullable().optional(),
  likes:z.number().nullable().optional(),
  videos:z.number().nullable().optional(),
  recentPosts:z.array(SocialPostSchema).default([]),
  accessible:z.boolean().optional(),
  error:z.string().optional(),
  collectionMethod:z.string().optional()
}).passthrough();

export const SocialSchema=z.object({
  ok:z.literal(true),
  observedAt:z.string().optional(),
  status:z.string().optional(),
  profiles:z.array(SocialProfileSchema).default([]),
  detectedProfileLinks:z.number().optional(),
  recentPostCount:z.number().optional(),
  limitations:z.array(z.string()).optional()
}).passthrough();

export const GoogleAdSchema=z.object({
  query:z.string().optional(),
  title:z.string().nullable().optional(),
  description:z.string().nullable().optional(),
  link:z.string().nullable().optional(),
  displayedLink:z.string().nullable().optional(),
  position:z.number().nullable().optional(),
  type:z.string().optional()
}).passthrough();

export const TransparencyResultSchema=z.object({
  title:z.string().nullable().optional(),
  link:z.string().optional(),
  snippet:z.string().nullable().optional(),
  position:z.number().nullable().optional()
}).passthrough();

export const AdsPayloadSchema=z.object({
  googleSearchAds:z.object({
    provider:z.string().optional(),
    connected:z.boolean().optional(),
    queries:z.array(z.string()).default([]),
    observedAds:z.array(GoogleAdSchema).default([]),
    activeObserved:z.boolean().optional(),
    observationCount:z.number().optional()
  }).passthrough().optional(),
  googleTransparency:z.object({
    status:z.string().optional(),
    results:z.array(TransparencyResultSchema).default([])
  }).passthrough().optional(),
  metaAdLibrary:z.object({
    status:z.string().optional(),
    searchUrl:z.string().optional(),
    results:z.array(TransparencyResultSchema).default([])
  }).passthrough().optional()
}).passthrough();

export const AdsSchema=z.object({
  ok:z.literal(true),
  observedAt:z.string().optional(),
  status:z.string().optional(),
  ads:AdsPayloadSchema.optional(),
  limitations:z.array(z.string()).optional()
}).passthrough();

export const ContentMixItemSchema=z.object({
  category:z.string(),
  sharePercent:z.number(),
  reason:BilingualTextSchema.optional()
}).passthrough();

export const StrategyPayloadSchema=z.object({
  primaryPositioning:BilingualTextSchema.optional(),
  secondaryPositioning:BilingualTextSchema.optional(),
  supportStrategy:BilingualTextSchema.optional(),
  contentMix:z.array(ContentMixItemSchema).default([]),
  platformRoles:z.array(z.object({platform:z.string(),role:BilingualTextSchema}).passthrough()).default([]),
  performanceObservations:z.array(BilingualTextSchema).default([]),
  summary:BilingualTextSchema.optional(),
  confidence:z.number().optional(),
  evidence:z.array(z.object({
    postUrl:z.string().optional(),
    category:z.string().optional(),
    reason:BilingualTextSchema.optional()
  }).passthrough()).default([])
}).passthrough();

export const StrategySchema=z.object({
  ok:z.literal(true),
  status:z.string().optional(),
  observedAt:z.string().optional(),
  postCount:z.number().optional(),
  strategy:StrategyPayloadSchema.nullable().optional(),
  limitations:z.array(z.string()).optional()
}).passthrough();

export const CompetitorSchema=z.object({
  domain:z.string(),
  url:z.string().optional(),
  title:z.string().optional(),
  platform:z.string().optional(),
  isEcommerce:z.boolean().optional(),
  relevance:z.number().optional(),
  matchedQueries:z.array(z.string()).default([]),
  searchAppearances:z.number().optional(),
  bestPosition:z.number().nullable().optional(),
  contentSimilarity:z.number().optional(),
  semanticClass:z.enum(['direct','adjacent','broad']).optional(),
  semanticReason:z.object({zh:z.string(),en:z.string()}).optional(),
  categoryOverlap:z.number().optional(),
  nicheOverlap:z.number().optional(),
  materialOverlap:z.number().optional(),
  matchedNicheTerms:z.array(z.string()).default([]),
  matchedCategoryGroups:z.array(z.string()).default([])
}).passthrough();

export const CompetitorDiscoverySchema=z.object({
  ok:z.literal(true),
  observedAt:z.string().optional(),
  market:z.string().optional(),
  queries:z.array(z.string()).default([]),
  candidatesDiscovered:z.number().optional(),
  candidatesVerified:z.number().optional(),
  directCompetitorCount:z.number().optional(),
  adjacentCompetitorCount:z.number().optional(),
  filteredBroadCount:z.number().optional(),
  quota:z.object({
    allowed:z.boolean(),
    used:z.number(),
    remaining:z.number(),
    resetAt:z.string(),
    windowSeconds:z.number(),
    limit:z.number()
  }).passthrough().optional(),
  competitors:z.array(CompetitorSchema).default([])
}).passthrough();

export const WatchlistBrandSchema=z.object({
  id:z.string().optional(),
  domain:z.string(),
  name:z.string().nullable().optional(),
  website_url:z.string().nullable().optional(),
  platform:z.string().nullable().optional()
}).passthrough();

export const WatchlistItemSchema=z.object({
  id:z.string().optional(),
  enabled:z.boolean().optional(),
  cadence:z.enum(['daily','weekly','monthly']).optional(),
  created_at:z.string().optional(),
  updated_at:z.string().nullable().optional(),
  last_run_at:z.string().nullable().optional(),
  last_success_at:z.string().nullable().optional(),
  last_status:z.enum(['never','running','success','partial','error','paused']).optional(),
  last_error:z.string().nullable().optional(),
  next_run_at:z.string().nullable().optional(),
  last_run_id:z.string().nullable().optional(),
  brands:WatchlistBrandSchema.nullable().optional()
}).passthrough();

export const WatchlistSchema=z.object({
  ok:z.literal(true),
  items:z.array(WatchlistItemSchema).default([])
}).passthrough();

export const WatchlistMutationSchema=z.object({
  ok:z.literal(true),
  action:z.string(),
  domain:z.string()
}).passthrough();

export const SnapshotSaveSchema=z.object({ok:z.literal(true),observedAt:z.string().optional()}).passthrough();
export const AnalysisRunSchema=z.object({
  ok:z.literal(true),
  runId:z.string(),
  status:z.string().optional(),
  run:z.unknown().optional()
}).passthrough();

export const HistoryTrendPointSchema=z.object({
  period:z.string(),
  observedAt:z.string(),
  value:z.number().nullable(),
  low:z.number().nullable(),
  high:z.number().nullable(),
  confidence:z.number().nullable(),
  modelVersion:z.string().nullable(),
  previousPct:z.number().nullable(),
  momPct:z.number().nullable(),
  yoyPct:z.number().nullable()
}).passthrough();

export const HistoryMetricTrendSchema=z.object({
  classification:z.enum(['observed','estimated','derived']),
  grain:z.enum(['daily','monthly','yearly']),
  status:z.enum(['ready','insufficient_history','no_data']),
  points:z.array(HistoryTrendPointSchema).default([]),
  latest:HistoryTrendPointSchema.nullable()
}).passthrough();

export const HistorySocialTrendSchema=HistoryMetricTrendSchema.extend({series:z.string()});

export const HistorySchema=z.object({
  ok:z.literal(true),
  snapshots:z.array(z.unknown()).default([]),
  products:z.array(z.unknown()).default([]),
  social:z.array(z.unknown()).default([]),
  ads:z.array(z.unknown()).default([]),
  seo:z.array(z.unknown()).default([]),
  traffic:z.array(z.unknown()).default([]),
  revenue:z.array(z.unknown()).default([]),
  economics:z.array(z.unknown()).default([]),
  meta:z.object({
    range:z.enum(['30d','90d','1y','all']),
    grain:z.enum(['daily','monthly','yearly']),
    rawSnapshotCount:z.number().nonnegative(),
    bucketCount:z.number().nonnegative()
  }).optional(),
  trends:z.object({
    traffic:HistoryMetricTrendSchema,
    revenue:HistoryMetricTrendSchema,
    productCount:HistoryMetricTrendSchema,
    seoSitemapUrls:HistoryMetricTrendSchema,
    grossMargin:HistoryMetricTrendSchema,
    socialFollowers:z.array(HistorySocialTrendSchema).default([])
  }).optional()
}).passthrough();

export type Positioning=z.infer<typeof PositioningSchema>;
export type Social=z.infer<typeof SocialSchema>;
export type Ads=z.infer<typeof AdsSchema>;
export type Strategy=z.infer<typeof StrategySchema>;
export type CompetitorDiscovery=z.infer<typeof CompetitorDiscoverySchema>;
export type Watchlist=z.infer<typeof WatchlistSchema>;
export type AnalyzeSite=z.infer<typeof AnalyzeSiteSchema>;
export type ProductSummary=z.infer<typeof ProductSummarySchema>;
export type Metrics=z.infer<typeof MetricsSchema>;
export type SeoEstimate=z.infer<typeof SeoEstimateSchema>;
export type TrafficEstimate=z.infer<typeof TrafficEstimateSchema>;
export type RevenueEstimate=z.infer<typeof RevenueEstimateSchema>;
export type EconomicsEstimate=z.infer<typeof EconomicsEstimateSchema>;
